import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { firstValueFrom } from 'rxjs';

import { EtapasBeneficioComponent } from '../../shared/components/etapas-beneficio/etapas-beneficio.component';
import { SessionService } from '../../core/services/session.service';
import { RequirementService } from '../../core/services/requirement.service';
import { ComposicaoAtoService } from '../../core/services/composicao-ato.service';
import { Rubrica } from '../../core/models/rubrica.model';

@Component({
  selector: 'app-composicao-ato',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatCheckboxModule,
    EtapasBeneficioComponent,
  ],
  templateUrl: './composicao-ato.component.html',
  styleUrl: './composicao-ato.component.scss',
})
export class ComposicaoAtoComponent implements OnInit {
  beneficiario: any = '';
  loading = false;
  rubricas: Rubrica[] = [];
  paragrafoAto: string = '';
  fonteValores = '';
  versao: number | null = null;
  novaCodigo = '';
  novaDescricao = '';
  novaValor = 0;

  displayedColumns: string[] = [
    'selecionar',
    'codigo',
    'descricao',
    'valor',
    'valorConsiderado',
    'acoes',
  ];

  @ViewChild('modalEditarRubrica') modalEditarRubrica!: TemplateRef<any>;
  @ViewChild('modalIncluirRubricas') modalIncluirRubricas!: TemplateRef<any>;
  private dialogRef?: MatDialogRef<any>;
  rubricaSelecionada: Rubrica | null = null;

  rubricasDisponiveis: Rubrica[] = [];

  // Campos do modal
  editDataInicioRecebimento: string = '';
  editDataDOE: string = '';
  editPagina: string = '';
  editFundamentacao: string = '';
  editValor: number = 0;

  salvando = false;

  gerandoDocumento = false;

  // O documento usa a última composição salva: conta as alterações feitas na tela
  // para avisar o que ainda não foi salvo (inclusive o que mudou durante o envio).
  private alteracoes = 0;
  private alteracoesSalvas = 0;

  get alteracoesNaoSalvas(): boolean {
    return this.alteracoes !== this.alteracoesSalvas;
  }

  marcarAlteracao(): void {
    this.alteracoes++;
  }

  constructor(
    private sessionService: SessionService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private composicaoService: ComposicaoAtoService,
    private requirementService: RequirementService
  ) {}

  ngOnInit(): void {
    this.beneficiario = this.sessionService.beneficiario;
    this.carregarRubricas();
  }

  carregarRubricas(): void {
    if (!this.beneficiario?.matricula || !this.beneficiario?.orgao?.id) return;
    this.loading = true;
    this.composicaoService.buscar(this.beneficiario.matricula,this.beneficiario.orgao.id).subscribe({
      next: data => {this.rubricas=data.rubricas ?? [];this.paragrafoAto=data.paragrafoAto ?? '';this.fonteValores=data.fonteValores ?? '';this.versao=data.versao;this.alteracoesSalvas=this.alteracoes;this.loading=false;},
      error: err => {
        this.loading=false;
        const motivo = motivoDoErro(err?.error);
        this.snackBar.open(motivo ? `Não foi possível carregar a composição salva: ${motivo}` : 'Não foi possível carregar a composição salva.','Fechar',{duration:7000});
      }
    });
  }

  abrirModalEditar(rubrica: Rubrica): void {
    this.rubricaSelecionada = rubrica;
    this.editDataInicioRecebimento = rubrica.dataInicioRecebimento || '';
    this.editDataDOE = rubrica.dataDOE || '';
    this.editPagina = rubrica.pagina || '';
    this.editFundamentacao = rubrica.fundamentacaoLegal || '';
    this.editValor = rubrica.valorConsiderado ?? rubrica.valor;

    this.dialogRef = this.dialog.open(this.modalEditarRubrica, {
      width: '600px',
      maxWidth: '95vw',
    });
  }

  salvarEdicaoRubrica(): void {
    if (!this.rubricaSelecionada) return;

    this.rubricaSelecionada.dataInicioRecebimento = this.editDataInicioRecebimento || null;
    this.rubricaSelecionada.dataDOE = this.editDataDOE || null;
    this.rubricaSelecionada.pagina = this.editPagina || null;
    this.rubricaSelecionada.fundamentacaoLegal = this.editFundamentacao;
    this.rubricaSelecionada.valorConsiderado = this.editValor;
    this.rubricaSelecionada.selecionada = true;
    this.marcarAlteracao();

    this.snackBar.open('Rubrica atualizada com sucesso', 'Fechar', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
    });

    this.dialogRef?.close();
  }

  toggleSelecao(rubrica: Rubrica): void {
    rubrica.selecionada = !rubrica.selecionada;
    if (rubrica.selecionada && rubrica.valorConsiderado === null) {
      rubrica.valorConsiderado = rubrica.valor;
    }
    this.marcarAlteracao();
  }

  calcularTotal(): number {
    return this.rubricas
      .filter((r) => r.selecionada)
      .reduce((sum, r) => sum + (r.valorConsiderado ?? r.valor), 0);
  }

  getRubricasSelecionadas(): Rubrica[] {
    return this.rubricas.filter((r) => r.selecionada);
  }

  salvar(): void {
    const selecionadas = this.getRubricasSelecionadas();

    if (selecionadas.length === 0) {
      this.snackBar.open('Selecione pelo menos uma rubrica', 'Fechar', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      });
      return;
    }

    this.salvando = true;

    const alteracoesEnviadas = this.alteracoes;
    const payload = {matricula:this.beneficiario.matricula,orgaoId:this.beneficiario.orgao?.id,
      rubricas:this.rubricas,paragrafoAto:this.paragrafoAto,fonteValores:this.fonteValores,versao:this.versao};
    this.composicaoService.salvar(payload).subscribe({
      next: data => {this.versao=data.versao;this.alteracoesSalvas=alteracoesEnviadas;this.salvando=false;this.snackBar.open('Composição salva.','Fechar',{duration:3000});},
      error: err => {this.salvando=false;this.snackBar.open(motivoDoErro(err?.error) ?? 'Não foi possível salvar a composição.','Fechar',{duration:7000});}
    });
  }

  limparSelecao(): void {
    this.rubricas.forEach((r) => {
      r.selecionada = false;
      r.valorConsiderado = null;
      r.dataInicioRecebimento = null;
      r.dataDOE = null;
      r.pagina = null;
    });
    this.paragrafoAto = '';
    this.marcarAlteracao();

    this.snackBar.open('Selecao limpa', 'Fechar', {
      duration: 2000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
    });
  }

  abrirModalIncluir(): void {this.novaCodigo='';this.novaDescricao='';this.novaValor=0;this.dialogRef=this.dialog.open(this.modalIncluirRubricas,{width:'600px'});}
  incluirRubricas(): void {
    if(!this.novaCodigo.trim() || !this.novaDescricao.trim() || !Number.isFinite(this.novaValor) || this.novaValor<0 || this.rubricas.some(r=>r.codigo===this.novaCodigo.trim())) {
      this.snackBar.open('Informe código único, descrição e valor não negativo.','Fechar',{duration:4000});return;
    }
    this.rubricas=[...this.rubricas,{id:Date.now(),codigo:this.novaCodigo.trim(),descricao:this.novaDescricao.trim(),valor:this.novaValor,valorConsiderado:this.novaValor,fundamentacaoLegal:'',dataInicioRecebimento:null,dataDOE:null,pagina:null,selecionada:true}];
    this.marcarAlteracao();
    this.dialogRef?.close();
  }

  async gerarDocumento(): Promise<void> {
    if (this.alteracoesNaoSalvas) {
      this.snackBar.open('Há alterações não salvas. O documento usa a última composição salva: clique em Salvar antes de gerar o documento.', 'Fechar', {
        duration: 7000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      });
      return;
    }

    this.gerandoDocumento = true;
    try {
      const blob = await firstValueFrom(this.composicaoService.quadro(this.beneficiario.matricula,this.beneficiario.orgao?.id));
      const file = new Blob([blob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quadro-discriminativo-${this.beneficiario.matricula}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      this.snackBar.open('Documento gerado com sucesso!', 'Fechar', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      });
    } catch (err: any) {
      console.error('Erro ao gerar documento:', err);
      const motivo = await motivoDoErroEmBlob(err?.error);
      this.snackBar.open(motivo ? `Não foi possível gerar o documento: ${motivo}` : 'Erro ao gerar documento. Tente novamente.', 'Fechar', {
        duration: 7000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      });
    } finally {
      this.gerandoDocumento = false;
    }
  }

  excluirRubrica(rubrica: Rubrica): void {
    this.rubricas = this.rubricas.filter(r => r !== rubrica);
    this.marcarAlteracao();
  }

  formatarValor(valor: number): string {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }
}

/**
 * Motivo devolvido pelo militar-back: ResponseStatusException e acesso negado vêm em "message";
 * erro de negócio e erro interno, em "mensagem"; ProblemDetail do Spring, em "detail".
 */
function motivoDoErro(corpo: any): string | null {
  const texto = corpo?.message ?? corpo?.mensagem ?? corpo?.detail;
  return typeof texto === 'string' && texto.trim() ? texto.trim() : null;
}

/** Em downloads (responseType 'blob') o corpo do erro chega como Blob com o JSON dentro. */
async function motivoDoErroEmBlob(corpo: unknown): Promise<string | null> {
  if (!(corpo instanceof Blob)) return motivoDoErro(corpo);
  try {
    return motivoDoErro(JSON.parse(await corpo.text()));
  } catch {
    return null;
  }
}
